'use client';

import { Skeleton } from '../../components/ui/skeleton';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../../components/ui/resizable';

interface DatasetSkeletonProps {
  /** Loading message if available */
  datasetName?: string;
  /** Whether the component is embedded in an iframe */
  isEmbedded?: boolean;
  /** Optional className for styling */
  className?: string;
}

/**
 * Dataset Skeleton Component
 * 
 * Shows a minimal loading skeleton that matches Nova's interface
 */
export function DatasetSkeleton({
  datasetName = 'Loading...',
  isEmbedded = false,
  className = '',
}: DatasetSkeletonProps) {
  // Apply full-size styles for embedded mode
  const embedStyles = isEmbedded ? 'h-[100vh] min-h-screen w-full' : '';
  
  return (
    <div className={`h-full w-full flex flex-col overflow-hidden ${embedStyles} ${className}`}>
      <ResizablePanelGroup direction="horizontal" className="w-full h-full">
        {/* Main Table Panel */}
        <ResizablePanel defaultSize={75} minSize={40}>
          <div className="h-full flex flex-col">
            {/* Minimal header similar to Nova's NovaHeader */}
            <div className="shrink-0 border-b bg-background px-2 py-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-4 w-24" />
                </div>
                <div className="flex items-center gap-2">
                  <Skeleton className="h-8 w-20" />
                  <Skeleton className="h-8 w-20" />
                  <Skeleton className="h-8 w-16" />
                </div>
              </div>
            </div>
            
            {/* Data grid skeleton area */}
            <div className="flex-1 overflow-hidden p-4">
              <div className="h-full space-y-3">
                {/* Column headers skeleton */}
                <div className="flex gap-3 border-b pb-3">
                  {Array(8).fill(0).map((_, i) => (
                    <Skeleton key={`header-${i}`} className="h-9 flex-1 min-w-0" />
                  ))}
                </div>
                
                {/* Rows skeleton */}
                {Array(12).fill(0).map((_, rowIndex) => (
                  <div key={`row-${rowIndex}`} className="flex gap-3 py-2">
                    {Array(8).fill(0).map((_, colIndex) => (
                      <Skeleton 
                        key={`cell-${rowIndex}-${colIndex}`} 
                        className="h-7 flex-1 min-w-0" 
                        style={{ 
                          opacity: Math.max(0.3, 1 - (rowIndex * 0.04)) // Better fade
                        }} 
                      />
                    ))}
                  </div>
                ))}
                
                {/* Footer area skeleton */}
                <div className="flex justify-between items-center pt-4 border-t">
                  <Skeleton className="h-6 w-32" />
                  <div className="flex gap-2">
                    <Skeleton className="h-8 w-16" />
                    <Skeleton className="h-8 w-16" />
                    <Skeleton className="h-8 w-20" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ResizablePanel>
        
        {/* Resizable Handle */}
        <ResizableHandle withHandle />
        
        {/* AI Chat Panel Skeleton */}
        <ResizablePanel defaultSize={25} minSize={20}>
          <div className="h-full flex flex-col border-l">
            {/* Chat header */}
            <div className="shrink-0 border-b bg-background p-3">
              <div className="flex items-center justify-between">
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-6 w-6 rounded-full" />
              </div>
            </div>
            
            {/* Chat messages area */}
            <div className="flex-1 overflow-hidden p-3 space-y-4">
              {/* Welcome message skeleton */}
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
              
              {/* Sample conversation skeleton */}
              {Array(3).fill(0).map((_, i) => (
                <div key={`msg-${i}`} className="space-y-2">
                  <div className="flex items-start gap-2">
                    <Skeleton className="h-6 w-6 rounded-full flex-shrink-0" />
                    <div className="flex-1 space-y-1">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Chat input area */}
            <div className="shrink-0 border-t p-3">
              <div className="space-y-2">
                <Skeleton className="h-10 w-full rounded-md" />
                <div className="flex justify-between items-center">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-8 w-16" />
                </div>
              </div>
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
} 